import { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Breadcrumb, Container, Row, Col, ListGroup, Image, Figure, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { RWebShare } from "react-web-share";
import { ContactContext } from "../context/ContactContext";
import Inquiry from "../sections/Inquiry";
import Slider from 'react-slick';
import FullFeatures from "../components/FullFeatures";
import FeatureIcons from "../components/FeatureIcons";
import YouTubeEmbed from "../components/YouTubeEmbed";
import { formatListingData, formatPrice } from "../Functions";
import ButtonArrow from "../components/ButtonArrow";
import "../assets/scss/pages/listing.scss";
import seal from "../assets/images/seal.svg";
import share from "../assets/images/share.svg";

const Listing = () =>  {
    const API = process.env.REACT_APP_API_BASE_URL;
    const APP = process.env.REACT_APP_MAIN;

    const contact = useContext(ContactContext);
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [largeImage, setLargeImage] = useState(null); // State to manage the large image
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" }); // State to manage fetchListing feedback

    const fetchListing = useCallback(async (listingID) => {
        try {
            const response = await fetch(`${API}/properties/${listingID}/`);

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error loading listings: ${response.status} ${response.statusText}`;
                setFetchFeedback({ success: false, message: "Unable to load properties, please refresh page."});
                console.error(errorMessage);
            }

            const data = await response.json();

            // Format API data
            const formattedData = formatListingData(data);
            // console.log(formattedData);
            setListing(formattedData);

            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured loading properties."});
            console.error(error);
        }
    }, [API]);

    // Fetch listing data from API
    useEffect(() => {
        fetchListing(id);
    }, [id, fetchListing]);

    // Destructure listing object
    const { property_name, price, description, state, city, land_use, land_title, bedrooms, bathrooms, terrace, land_size, features, newly_built, furnished, serviced, payment_plan, images, youtube_url } = listing || {};

    const property_url = `${APP}/properties/${id}`;

    // Handle initial large image
    useEffect(() => {
        if (listing?.images && listing.images.length > 0) {
            setLargeImage(images[0]); // Set large image to first image on listing load
        }
    }, [listing, images]);

    // Manage click event on thumbnail image
    const handleThumbnailClick = (index) => {
        setLargeImage(listing.images[index]);
    };

    const hasEnoughReviewForSlider = listing?.images.length > 1;
    // Thumbnail slider settings
    const sliderSettings = {
        infinite: hasEnoughReviewForSlider,
        speed: 2000,
        slidesToShow: 5, // Display 5 thumbnails for screen sizes greater than Bootstrap's sm
        slidesToScroll: 1,
        autoplay: hasEnoughReviewForSlider,
        autoplaySpeed: 2000,
        arrows: true,
        beforeChange: (currentIndex, nextIndex) => { // Sets figure image to current slider image
            if (listing?.images) {
                setLargeImage(listing.images[currentIndex]);
            }
        },
        responsive: [
            {
                breakpoint: 576,
                settings: {
                    slidesToShow: 4, // Display 4 thumbnails for sm screen sizes
                },
            },
        ]
    };

    // Property Action Buttons are handled here: Make Inquiry via WhatsApp or Schedule a Visit
    const handleInquiry = (type) => {
        if (contact && contact.phone_number) {
            const phoneNumber = contact?.phone_number.split(",")[0].replace(/[+\s]/g, ""); // Select the first phone number and remove + and spaces
            // console.log(phoneNumber);

            if (phoneNumber.length > 0) {
                const message = `Hi Afinney Realty. I'll like to ${(type === "inquiry") ? "enquire about your" : "schedule a viewing of this"} ${formatPrice(price)} ${property_name} in ${city}, ${state}.\n\n${property_url} ${(type === "inspection") ? "\n\nI will be available on __________ date at _________ time." : ""}`;
                const encodedMessage = encodeURIComponent(message);
                const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                window.open(whatsappUrl, "_blank")
            } else {
                console.error("No phone numbers found in contact information.");
            }
        } else {
            console.error("Contact information or phone number is missing.");
        }
    }

    // Renders the Tooltip component for description on share button
    const renderTooltip = (props) => {
        return (
            <Tooltip id="button-tooltip" {...props}>
                Share this property
            </Tooltip>
        )
    }

    return (
        <Container className="listing min-vh-100">
            {listing ?
                <>
                    {/* Navigation section */}
                    <Breadcrumb className="py-5 mx-auto">
                        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                        <Breadcrumb.Item href="/properties">Property Listings</Breadcrumb.Item>
                    </Breadcrumb>

                    {/* Header section */}
                    <Col as="header" className="d-flex flex-column gap-2 my-2 pb-4 border-bottom">
                        <h1 className="text-secondary fw-semibold">{ property_name }</h1>
                        <h5 className="location text-uppercase">IN { city }, { state }</h5>

                        {/* Labels */}
                        <ListGroup className="property-label d-flex flex-row border-0 align-items-center gap-2">
                            { land_use && <ListGroup.Item className="bg-secondary rounded-0">{ land_use.toUpperCase() }</ListGroup.Item> }
                            { serviced && <ListGroup.Item className="bg-secondary">FULLY SERVICED</ListGroup.Item> }
                            <ListGroup.Item className="build-status rounded-0">
                                { newly_built ? "NEWLY BUILT" : "RESALE" }
                            </ListGroup.Item>
                            { furnished && <ListGroup.Item className="bg-primary rounded-0">FURNISHED"</ListGroup.Item> }
                        </ListGroup>

                        {/* Share Button & Price */}
                        <ListGroup className="d-flex flex-row align-items-center position-relative my-2">
                            {/* Share */}
                            <ListGroup.Item className="d-flex border-0">
                            { id && (
                                <RWebShare
                                    data={{
                                        text: `Property - ${property_name }`,
                                        url: property_url,
                                        title: "Share property from Afinney Realty",
                                    }}
                                    onClick={() => console.log(`${property_name} shared successfully`)}
                                >
                                    <OverlayTrigger
                                        placement="top" // Position of the tooltip (top, right, bottom, left)
                                        delay={{ show: 250, hide: 400 }} // Delay in ms before showing/hiding
                                        overlay={ renderTooltip }
                                    >
                                        <Image id="share-button" src={share} />
                                    </OverlayTrigger>
                                </RWebShare>
                            )}
                            </ListGroup.Item>

                            {/* Price */}
                            <ListGroup.Item className="h2 text-secondary fw-bold fs-2 px-2 position-absolute end-0 border-0"> { formatPrice(price) }</ListGroup.Item>
                        </ListGroup>
                    </Col>

                    {/* Property images & info */}
                    <Row as="section" className="listing-section property-info d-flex justify-content-between py-3">
                        {/* Image section */}
                        <Col className="images px-md-0 d-flex flex-column gap-3">
                            {/* Thumbnail slider */}
                            <Slider { ...sliderSettings } className="slider m-0">
                                {images.length > 0 &&
                                    images.map((image, index) => (
                                        <Col key={ index } className="px-0">
                                            <Image
                                              src={ image }
                                              alt={`Property thumbnail ${index + 1}`}
                                              className="rounded-4 mx-auto"
                                              onClick={() => handleThumbnailClick(index)} // Update the large image
                                            />
                                        </Col>
                                    ))
                                }
                            </Slider>

                            {/* Large image */}
                            <Figure.Image
                                className="rounded-4 border"
                                src={ largeImage && largeImage }
                                alt="Selected property image"
                            />

                            {/* Property action buttons */}
                            <Row className="actions my-3 d-flex flex-row justify-content-center gap-2">
                                {/* Make Inquiry Button */}
                                <Button onClick={ () => handleInquiry("inquiry") } className="px-4 py-2">Make Inquiry<ButtonArrow /></Button>
                                <Button onClick={ () => handleInquiry("inspection") }className="card-button border border-primary px-4 py-2">Schedule a Visit<ButtonArrow /></Button>
                            </Row>
                        </Col>

                        {/* Feature sidebar */}
                        <Col as="aside" className="listing-section feature-sidebar px-md-0 mt-3 mt-lg-0 d-flex flex-column align-items-center">
                            <p className="land-title d-flex align-items-center border w-100 px-4 px-lg-5 py-3 mb-1 mb-lg-3">
                                <Image src={ seal } />{ land_title }
                            </p>
                            <FeatureIcons text={true} numOfBedrooms={ bedrooms } numOfBathrooms={ bathrooms} numOfTerraces={ terrace } landSize={ land_size } />
                        </Col>
                    </Row>

                    {/* YouTube embeded video */}
                    { youtube_url &&
                        <Row as="section" className="listing-section d-flex justify-content-between">
                            <h2 className="text-primary text-center mb-3"><b>PROPERTY</b> TOUR</h2>
                            <p className="text-secondary px-5 text-center mb-5">Watch the full house tour below:</p>
                            <YouTubeEmbed url={ youtube_url } propertyName={ property_name }/>
                        </Row>
                    }

                    {/* Features and Amenities */}
                    { features.length > 0 &&
                        <Row as="section" className="listing-section d-flex justify-content-between">
                            <h2 className="text-primary text-center mb-5"><b>FEATURES</b> & AMENITIES</h2>
                            <FullFeatures features={ features } />
                        </Row>
                    }

                    {/* Payment plans */}
                    { payment_plan.length > 0 &&
                        <Row as="section" className="listing-section d-flex justify-content-between">
                            <h2 className="text-primary text-center mb-3"><b>PAYMENT</b> PLAN</h2>
                            <p className="text-secondary px-5 text-center mb-5">Available payment plans for this property includes:</p>
                            <Row md={2} lg={3} className="d-flex flex-row align-items-center justify-content-center gap-3 payment-plans">
                                { payment_plan.map((plan, index) => (
                                    <Col as="h5" key={index} className="plan rounded-4 text-center py-4" xs={10} sm={7} md={5} lg={3}>
                                        { plan }
                                    </Col>
                                ))}
                            </Row>
                        </Row>
                    }

                    {/* Property description */}
                    <Row as="section" className="listing-section d-flex justify-content-center gap-1 mx-sm-2 mx-md-5 mx-lg-5">
                        <h2 className="text-primary text-center mb-5"><b>DESCRIPTION</b></h2>
                        <p className="text-secondary px-5 text-center">{ description }</p>
                    </Row>

                    {/* Inquiry section */}
                    <Inquiry contactCardType="text" />
                </> :
                <p className={`text-center fw-semibold ${ (fetchFeedback.success || fetchFeedback.success == null) ? "text-primary" : "text-danger" }`}>{fetchFeedback.message || "Fetching property data..."}</p>
            }
        </Container>
    );
};

export default Listing;
